/**
 * Role-based route access configuration.
 * Maps Cognito groups to permitted route patterns and default redirects.
 * Supports dynamic role loading from API with static fallback.
 */

import type { RoleDefinition } from '@api/endpoints/onboardingApi';

// Completed authoritative role→route map (Component Access Control, Decision 8,
// Req 19.5, 19.6). Every Component_Entry_Route is explicitly granted to the
// Roles in that Component's default policy, and this map agrees 1:1 with
// STATIC_NAV_ITEMS in navConfig.ts. Retained ONLY as the Default_Access_Policy
// seed source (Req 10.3) — once a practice Access_Policy exists, the Entry_Gate
// / policy is the runtime source of truth, not this map.
//
// standard-user includes /alarms (standard users work alarm findings) but
// deliberately omits /metrics (which defaults to practice-owner only);
// data_uploader omits /home (lands at /data, Req 22.2/22.5).
export const STATIC_ROLE_ROUTE_MAP: Record<string, string[]> = {
  'medrender-owner': ['**'],
  'practice-owner': [
    '/home',
    '/surveys', '/surveys/**',
    '/visits',
    '/provider-scorecard',
    '/metrics', '/metrics/**',
    '/alarms', '/alarms/**',
    '/worklists',
    '/goal-sets', '/goal-sets/**',
    '/reminders', '/reminders/**',
    '/data', '/data/**',
    '/configure', '/configure/**',
    '/manage-users',
  ],
  'standard-user': [
    '/home',
    '/surveys', '/surveys/**',
    '/visits',
    '/provider-scorecard',
    '/alarms', '/alarms/**',
    '/worklists',
    '/goal-sets', '/goal-sets/**',
    '/reminders', '/reminders/**',
    '/data', '/data/**',
  ],
  'data_uploader': ['/data', '/data/**'],
};

export const STATIC_DEFAULT_REDIRECT: Record<string, string> = {
  'medrender-owner': '/home',
  'practice-owner': '/home',
  'standard-user': '/home',
  'data_uploader': '/data',
};

/**
 * Builds a route map from dynamic role definitions.
 */
export function buildRouteMap(roles: RoleDefinition[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const role of roles) {
    map[role.roleId] = role.allowedRoutes;
  }
  return map;
}

/**
 * Builds a default redirect map from dynamic role definitions.
 */
export function buildDefaultRedirects(roles: RoleDefinition[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const role of roles) {
    map[role.roleId] = role.defaultRedirect;
  }
  return map;
}

/**
 * Returns all route patterns accessible by the given groups.
 */
export function getAccessibleRoutes(groups: string[], routeMap?: Record<string, string[]>): string[] {
  const map = routeMap ?? STATIC_ROLE_ROUTE_MAP;
  const routes = new Set<string>();
  for (const group of groups) {
    const patterns = map[group];
    if (patterns) {
      patterns.forEach((p) => routes.add(p));
    }
  }
  return Array.from(routes);
}

/**
 * Checks if a user with the given groups can access a specific path.
 */
export function canAccessRoute(groups: string[], path: string, routeMap?: Record<string, string[]>): boolean {
  const map = routeMap ?? STATIC_ROLE_ROUTE_MAP;
  for (const group of groups) {
    const patterns = map[group];
    if (!patterns) continue;
    for (const pattern of patterns) {
      if (pattern === '**') return true;
      if (pattern === path) return true;
      if (pattern.endsWith('/**')) {
        const prefix = pattern.slice(0, -3);
        if (path === prefix || path.startsWith(prefix + '/')) return true;
      }
    }
  }
  return false;
}

/**
 * Returns the default redirect path for the first matching group.
 */
export function getDefaultRedirect(groups: string[], redirectMap?: Record<string, string>): string {
  const map = redirectMap ?? STATIC_DEFAULT_REDIRECT;
  for (const group of groups) {
    if (map[group]) return map[group];
  }
  return '/no-access';
}
